import { useState, useEffect } from "react";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
const APP_URL = "https://app.fieldjob.getdiswayne.com";

// ─── FETCH SITE CONTENT FROM SUPABASE ────────────────────────────────────────
async function fetchContent(section) {
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/fj_site_content?section=eq.${section}&select=content`, {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }
    });
    const data = await res.json();
    return data?.[0]?.content || null;
  } catch { return null; }
}

// ─── SHARED CSS ───────────────────────────────────────────────────────────────
const globalCss = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --ink: #0f1923; --ink2: #162030; --gold: #c8972a; --gold-l: #e4ae3a;
    --gold-p: rgba(200,151,42,0.10); --gold-b: rgba(200,151,42,0.25);
    --emerald: #10b981; --amber: #f59e0b; --white: #ffffff; --off: #f8f9fb; --off2: #f1f4f8;
    --text: #1a2535; --muted: #64748b; --border: #e4e9f0;
  }
  body { font-family: 'IBM Plex Sans', sans-serif; color: var(--text); background: var(--white); overflow-x: hidden; }
  a { text-decoration: none; }
  button { font-family: 'IBM Plex Sans', sans-serif; }

  /* NAV */
  .fj-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 300;
    background: white; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 48px; height: 64px;
    box-shadow: 0 1px 12px rgba(0,0,0,0.06);
  }
  .fj-logo { display: flex; align-items: center; gap: 10px; cursor: pointer; background: none; border: none; padding: 0; }
  .fj-logomark {
    width: 34px; height: 34px; border-radius: 9px; background: var(--ink);
    border: 1px solid rgba(200,151,42,0.4); display: flex; align-items: center; justify-content: center;
    box-shadow: 0 2px 8px rgba(200,151,42,0.15), inset 0 1px 0 rgba(255,255,255,0.08);
    position: relative; overflow: hidden; flex-shrink: 0;
  }
  .fj-logomark::after {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 50%;
    background: linear-gradient(rgba(255,255,255,0.07), transparent);
    border-radius: 9px 9px 0 0;
  }
  .fj-logomark svg { width: 20px; height: 20px; position: relative; z-index: 1; }
  .fj-logotext { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 700; color: var(--ink); }
  .fj-navlinks { display: flex; gap: 28px; }
  .fj-navlinks button { background: none; border: none; color: var(--muted); font-size: 13.5px; font-weight: 500; cursor: pointer; padding: 0; transition: color 0.2s; }
  .fj-navlinks button:hover, .fj-navlinks button.active { color: var(--ink); }
  .fj-navcta { display: flex; gap: 10px; align-items: center; }
  .btn-ghost { color: var(--ink); font-size: 13px; font-weight: 600; padding: 8px 20px; border: 1.5px solid var(--border); border-radius: 8px; background: white; cursor: pointer; transition: all 0.2s; }
  .btn-ghost:hover { border-color: var(--ink); }
  .btn-fill { background: var(--ink); color: white; font-size: 13px; font-weight: 700; padding: 9px 22px; border-radius: 8px; border: none; cursor: pointer; transition: all 0.2s; }
  .btn-fill:hover { background: var(--ink2); }

  /* HAMBURGER */
  .fj-hamburger { display: none; flex-direction: column; gap: 5px; background: none; border: none; cursor: pointer; padding: 8px; }
  .fj-hamburger span { display: block; width: 22px; height: 2px; background: var(--ink); border-radius: 2px; transition: all 0.25s; transform-origin: center; }
  .fj-mobile-menu { display: none; position: fixed; top: 64px; left: 0; right: 0; background: white; border-bottom: 1px solid var(--border); z-index: 299; padding: 16px 24px; flex-direction: column; gap: 4px; box-shadow: 0 8px 24px rgba(0,0,0,0.08); max-height: 0; overflow: hidden; transition: max-height 0.3s ease; }
  .fj-mobile-menu.open { max-height: 600px; }
  .fj-mobile-menu button { background: none; border: none; padding: 12px 0; font-size: 15px; font-weight: 500; color: var(--text); cursor: pointer; text-align: left; border-bottom: 1px solid var(--border); font-family: 'IBM Plex Sans', sans-serif; }
  .fj-mobile-menu button:last-child { border-bottom: none; }
  .fj-mobile-menu .mob-cta { color: var(--gold); font-weight: 700; border-bottom: none; }

  /* SHARED SECTIONS */
  .eyebrow { font-size: 10.5px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold); margin-bottom: 12px; }
  .sec-title { font-family: 'Playfair Display', serif; font-size: clamp(28px, 3.5vw, 44px); color: var(--ink); font-weight: 700; line-height: 1.2; margin-bottom: 14px; }
  .sec-sub { font-size: 15px; color: var(--muted); max-width: 560px; line-height: 1.75; margin-bottom: 48px; font-weight: 300; }
  .sec-inner { max-width: 1200px; margin: 0 auto; }

  /* BUTTONS */
  .btn-primary { background: var(--ink); color: white; padding: 13px 28px; border-radius: 8px; font-size: 14px; font-weight: 700; border: none; cursor: pointer; transition: all 0.25s; }
  .btn-primary:hover { background: var(--ink2); transform: translateY(-2px); box-shadow: 0 8px 24px rgba(15,25,35,0.2); }
  .btn-secondary { background: white; color: var(--ink); padding: 13px 28px; border-radius: 8px; font-size: 14px; font-weight: 600; border: 1.5px solid var(--border); cursor: pointer; transition: all 0.2s; }
  .btn-secondary:hover { border-color: var(--gold); color: var(--gold); }
  .btn-gold { background: var(--gold); color: var(--ink); padding: 13px 28px; border-radius: 8px; font-size: 14px; font-weight: 700; border: none; cursor: pointer; transition: all 0.2s; }
  .btn-gold:hover { background: var(--gold-l); }

  /* HERO */
  .hero { background: var(--off); padding: 120px 56px 80px; position: relative; overflow: hidden; }
  .hero::before { content: ''; position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse 50% 60% at 80% 40%, rgba(200,151,42,0.06) 0%, transparent 70%), radial-gradient(ellipse 30% 40% at 5% 80%, rgba(15,25,35,0.04) 0%, transparent 60%); }
  .hero::after { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, var(--ink) 0%, var(--gold) 50%, var(--ink) 100%); }
  .hero-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 55% 1fr; gap: 64px; align-items: center; position: relative; }
  .hero-tag { display: inline-flex; align-items: center; gap: 8px; background: white; border: 1px solid var(--border); border-radius: 100px; padding: 6px 16px; font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--muted); margin-bottom: 24px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
  .hero h1 { font-family: 'Playfair Display', serif; font-size: clamp(38px, 4.5vw, 60px); color: var(--ink); font-weight: 700; line-height: 1.1; margin-bottom: 22px; letter-spacing: -0.3px; }
  .hero h1 em { color: var(--gold); font-style: italic; }
  .hero-sub { font-size: 16px; color: var(--muted); line-height: 1.8; margin-bottom: 40px; font-weight: 300; max-width: 480px; }
  .hero-btns { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
  .hero-cards { display: flex; flex-direction: column; gap: 12px; }
  .hc { background: white; border: 1px solid var(--border); border-radius: 14px; padding: 18px 20px; box-shadow: 0 2px 12px rgba(0,0,0,0.05); display: flex; align-items: center; gap: 16px; transition: all 0.2s; }
  .hc:hover { box-shadow: 0 4px 24px rgba(0,0,0,0.1); transform: translateX(4px); }
  .hc-icon { width: 42px; height: 42px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: var(--ink); border: 1px solid rgba(200,151,42,0.35); box-shadow: 0 2px 8px rgba(200,151,42,0.12), inset 0 1px 0 rgba(255,255,255,0.07); position: relative; overflow: hidden; }
  .hc-icon::after { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 50%; background: linear-gradient(rgba(255,255,255,0.07), transparent); border-radius: 10px 10px 0 0; pointer-events: none; }
  .hc-icon svg { width: 18px; height: 18px; stroke: var(--gold); position: relative; z-index: 1; }
  .hc-title { font-size: 13.5px; font-weight: 700; color: var(--ink); margin-bottom: 3px; }
  .hc-sub { font-size: 12px; color: var(--muted); }
  .hc-val { font-size: 18px; font-weight: 700; margin-left: auto; flex-shrink: 0; }

  /* TRUST STRIP */
  .trust-strip { background: white; border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); padding: 20px 56px; }
  .trust-inner { max-width: 1200px; margin: 0 auto; display: flex; align-items: center; gap: 40px; }
  .trust-label { font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--muted); white-space: nowrap; }
  .trust-div { width: 1px; height: 20px; background: var(--border); flex-shrink: 0; }
  .trust-items { display: flex; align-items: center; gap: 28px; flex-wrap: wrap; }
  .trust-item { font-size: 12px; font-weight: 600; color: #64748b; display: flex; align-items: center; gap: 9px; transition: color 0.2s; cursor: default; }
  .trust-item:hover { color: var(--ink); }
  .trust-item:hover .trust-icon { background: var(--ink); border-color: rgba(200,151,42,0.5); }
  .trust-item:hover .trust-icon svg { stroke: var(--gold); }
  .trust-icon { width: 28px; height: 28px; border-radius: 7px; flex-shrink: 0; background: var(--off2); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
  .trust-icon svg { width: 13px; height: 13px; stroke: #64748b; transition: stroke 0.2s; }

  /* FEATURES */
  .features-section { background: var(--white); padding: 96px 56px; }
  .feat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
  .feat { background: var(--off); border: 1px solid var(--border); border-radius: 14px; padding: 24px; transition: all 0.25s; position: relative; cursor: default; }
  .feat:hover { background: white; border-color: var(--gold-b); box-shadow: 0 8px 32px rgba(200,151,42,0.08); transform: translateY(-2px); }
  .feat:hover .feat-title { color: var(--gold); }
  .feat-num { font-size: 10px; font-weight: 700; letter-spacing: 2px; color: var(--muted); margin-bottom: 16px; opacity: 0.5; }
  .feat-icon { width: 40px; height: 40px; border-radius: 10px; background: white; border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; margin-bottom: 14px; }
  .feat-title { font-size: 14px; font-weight: 700; color: var(--ink); margin-bottom: 8px; transition: color 0.25s; }
  .feat-desc { font-size: 12.5px; color: var(--muted); line-height: 1.65; }

  /* PRICING — 4 card grid */
  .pricing-section { background: var(--ink); padding: 96px 56px; }
  .pricing-top { text-align: center; margin-bottom: 56px; }
  .pricing-top .sec-title { color: white; }
  .pricing-top .sec-sub { color: rgba(255,255,255,0.45); max-width: 560px; margin: 0 auto 0; }
  .pricing-top .eyebrow { color: var(--gold); }
  .plans-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; max-width: 1200px; margin: 0 auto; }
  .plan-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 20px; padding: 32px 28px; display: flex; flex-direction: column; position: relative; transition: all 0.25s; }
  .plan-card:hover { background: rgba(255,255,255,0.07); transform: translateY(-3px); }
  .plan-card.highlighted { background: white; border: 2px solid var(--gold); }
  .plan-card.highlighted:hover { transform: translateY(-4px); box-shadow: 0 24px 64px rgba(200,151,42,0.2); }
  .plan-badge { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: var(--gold); color: var(--ink); font-size: 10px; font-weight: 700; letter-spacing: 1px; padding: 4px 14px; border-radius: 100px; white-space: nowrap; }
  .plan-name { font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,0.5); margin-bottom: 16px; }
  .plan-card.highlighted .plan-name { color: var(--muted); }
  .plan-price-row { display: flex; align-items: baseline; gap: 2px; margin-bottom: 4px; }
  .plan-cur { font-size: 18px; font-weight: 700; color: white; margin-top: 6px; }
  .plan-card.highlighted .plan-cur { color: var(--ink); }
  .plan-amt { font-family: 'Playfair Display', serif; font-size: 52px; font-weight: 700; color: white; line-height: 1; }
  .plan-card.highlighted .plan-amt { color: var(--ink); }
  .plan-amt.custom { font-size: 28px; padding-top: 10px; }
  .plan-period { font-size: 12px; color: rgba(255,255,255,0.35); margin-bottom: 24px; }
  .plan-card.highlighted .plan-period { color: var(--muted); }
  .plan-divider { height: 1px; background: rgba(255,255,255,0.1); margin-bottom: 20px; }
  .plan-card.highlighted .plan-divider { background: var(--border); }
  .plan-feats { display: flex; flex-direction: column; gap: 9px; margin-bottom: 28px; flex: 1; }
  .pf { display: flex; align-items: flex-start; gap: 9px; font-size: 13px; color: rgba(255,255,255,0.7); }
  .plan-card.highlighted .pf { color: var(--text); }
  .pf-dot { width: 16px; height: 16px; border-radius: 50%; background: rgba(200,151,42,0.15); border: 1px solid rgba(200,151,42,0.3); display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
  .pf-dot svg { width: 9px; height: 9px; stroke: var(--gold); }
  .plan-card.highlighted .pf-dot { background: rgba(16,185,129,0.12); border-color: rgba(16,185,129,0.3); }
  .pf-dot.highlighted svg { stroke: var(--emerald); }
  .plan-cta { width: 100%; padding: 13px; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s; border: none; }
  .plan-cta.outline { background: transparent; border: 1.5px solid rgba(255,255,255,0.2); color: white; }
  .plan-cta.outline:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.4); }
  .plan-cta.gold { background: var(--gold); color: var(--ink); }
  .plan-cta.gold:hover { background: var(--gold-l); }
  .plan-note { text-align: center; font-size: 11px; color: rgba(255,255,255,0.25); margin-top: 10px; }
  .plan-card.highlighted .plan-note { color: var(--muted); }

  /* PRE-FOOTER */
  .prefooter { background: var(--off); padding: 80px 56px 60px; border-top: 1px solid var(--border); }
  .prefooter-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1.5fr repeat(3, 1fr); gap: 48px; }
  .prefooter-brand .fj-logotext { font-size: 22px; }
  .prefooter-tagline { font-size: 13px; color: var(--muted); line-height: 1.7; margin: 12px 0 20px; max-width: 240px; }
  .prefooter-col-title { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--ink); margin-bottom: 16px; }
  .prefooter-links { display: flex; flex-direction: column; gap: 10px; }
  .prefooter-links button { background: none; border: none; padding: 0; font-size: 13.5px; color: var(--muted); cursor: pointer; text-align: left; transition: color 0.2s; font-family: 'IBM Plex Sans', sans-serif; }
  .prefooter-links button:hover { color: var(--ink); }

  /* FOOTER */
  .fj-footer { background: var(--ink); padding: 24px 56px; border-top: 1px solid rgba(255,255,255,0.05); }
  .footer-inner { max-width: 1200px; margin: 0 auto; display: flex; justify-content: space-between; align-items: center; gap: 24px; flex-wrap: wrap; }
  .footer-logo { display: flex; align-items: center; gap: 10px; }
  .f-mark { width: 28px; height: 28px; border-radius: 7px; background: rgba(200,151,42,0.15); border: 1px solid var(--gold-b); display: flex; align-items: center; justify-content: center; }
  .f-mark svg { width: 16px; height: 16px; }
  .f-name { font-family: 'Playfair Display', serif; font-size: 16px; color: white; }
  .f-copy { font-size: 11px; color: rgba(255,255,255,0.2); }

  /* CTA SECTION */
  .cta-section { background: var(--off); padding: 96px 56px; text-align: center; }
  .cta-inner { max-width: 700px; margin: 0 auto; }
  .cta-btns { display: flex; gap: 12px; justify-content: center; margin-top: 32px; flex-wrap: wrap; }

  /* PAGE HERO (inner pages) */
  .page-hero { background: var(--ink); padding: 96px 56px 80px; position: relative; overflow: hidden; }
  .page-hero::after { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, var(--ink) 0%, var(--gold) 50%, var(--ink) 100%); }
  .page-hero-inner { max-width: 1200px; margin: 0 auto; }
  .page-hero .eyebrow { color: rgba(200,151,42,0.7); }
  .page-hero .sec-title { color: white; }
  .page-hero .sec-sub { color: rgba(255,255,255,0.45); margin-bottom: 0; }

  /* FEATURES PAGE */
  .features-page { padding: 80px 56px; }
  .feat-page-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  .feat-page-card { background: var(--off); border: 1px solid var(--border); border-radius: 16px; padding: 28px; transition: all 0.25s; }
  .feat-page-card:hover { background: white; border-color: var(--gold-b); box-shadow: 0 8px 32px rgba(200,151,42,0.08); transform: translateY(-2px); }
  .feat-page-icon { width: 48px; height: 48px; border-radius: 12px; background: var(--ink); border: 1px solid rgba(200,151,42,0.3); display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
  .feat-page-icon svg { width: 22px; height: 22px; stroke: var(--gold); }
  .feat-page-title { font-size: 16px; font-weight: 700; color: var(--ink); margin-bottom: 8px; }
  .feat-page-desc { font-size: 13.5px; color: var(--muted); line-height: 1.7; }

  /* PLATFORMS PAGE */
  .platforms-page { padding: 80px 56px; }
  .platforms-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
  .platform-card { border: 1px solid var(--border); border-radius: 20px; overflow: hidden; transition: all 0.25s; }
  .platform-card:hover { box-shadow: 0 12px 40px rgba(0,0,0,0.1); transform: translateY(-3px); }
  .platform-top { background: var(--ink); padding: 32px 28px; position: relative; }
  .platform-name { font-family: 'Playfair Display', serif; font-size: 24px; color: white; margin-bottom: 6px; }
  .platform-tagline { font-size: 13px; color: rgba(255,255,255,0.45); }
  .platform-badge { display: inline-flex; align-items: center; gap: 6px; margin-top: 16px; padding: 4px 12px; border-radius: 100px; font-size: 11px; font-weight: 700; }
  .platform-badge.green { background: rgba(16,185,129,0.15); color: var(--emerald); border: 1px solid rgba(16,185,129,0.3); }
  .platform-badge.amber { background: rgba(245,158,11,0.15); color: var(--amber); border: 1px solid rgba(245,158,11,0.3); }
  .platform-body { padding: 28px; background: white; }
  .platform-desc { font-size: 14px; color: var(--muted); line-height: 1.7; margin-bottom: 20px; }
  .platform-highlights { display: flex; flex-direction: column; gap: 8px; }
  .platform-hl { display: flex; align-items: flex-start; gap: 10px; font-size: 13px; color: var(--text); }
  .platform-hl-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--gold); flex-shrink: 0; margin-top: 6px; }

  /* INTEGRATIONS PAGE */
  .integrations-page { padding: 80px 56px; }
  .integrations-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
  .integration-card { background: var(--off); border: 1px solid var(--border); border-radius: 14px; padding: 22px; transition: all 0.2s; }
  .integration-card:hover { background: white; border-color: var(--gold-b); box-shadow: 0 4px 20px rgba(200,151,42,0.07); }
  .int-cat { font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--muted); margin-bottom: 10px; }
  .int-name { font-size: 15px; font-weight: 700; color: var(--ink); margin-bottom: 6px; }
  .int-desc { font-size: 12.5px; color: var(--muted); line-height: 1.6; margin-bottom: 12px; }
  .int-badge { display: inline-block; font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 100px; }
  .int-badge.live { background: rgba(16,185,129,0.12); color: var(--emerald); }
  .int-badge.soon { background: rgba(245,158,11,0.12); color: var(--amber); }

  /* TEMPLATES PAGE */
  .templates-page { padding: 80px 56px; }
  .templates-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  .template-card { border: 1px solid var(--border); border-radius: 16px; overflow: hidden; transition: all 0.25s; cursor: default; }
  .template-card:hover { box-shadow: 0 8px 32px rgba(0,0,0,0.1); transform: translateY(-2px); }
  .template-preview { height: 140px; display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; }
  .template-preview-inner { width: 80px; height: 100px; border-radius: 8px; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); display: flex; flex-direction: column; padding: 10px 8px; gap: 5px; }
  .template-preview-line { height: 6px; border-radius: 3px; background: rgba(255,255,255,0.5); }
  .template-preview-line.short { width: 50%; }
  .template-preview-line.tiny { width: 70%; height: 4px; background: rgba(255,255,255,0.25); }
  .template-info { padding: 18px 20px; background: white; }
  .template-cat { font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold); margin-bottom: 6px; }
  .template-name { font-size: 14px; font-weight: 700; color: var(--ink); margin-bottom: 6px; }
  .template-desc { font-size: 12.5px; color: var(--muted); line-height: 1.6; }

  /* FAQ PAGE */
  .faq-page { padding: 80px 56px; }
  .faq-list { max-width: 720px; display: flex; flex-direction: column; gap: 0; }
  .faq-item { border-bottom: 1px solid var(--border); }
  .faq-q { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 22px 0; cursor: pointer; }
  .faq-q-text { font-size: 15px; font-weight: 600; color: var(--ink); }
  .faq-chevron { width: 20px; height: 20px; border-radius: 50%; background: var(--off2); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.2s; }
  .faq-chevron.open { background: var(--ink); border-color: var(--ink); transform: rotate(180deg); }
  .faq-chevron svg { width: 10px; height: 10px; stroke: var(--muted); transition: stroke 0.2s; }
  .faq-chevron.open svg { stroke: white; }
  .faq-a { font-size: 14px; color: var(--muted); line-height: 1.8; padding-bottom: 20px; }

  /* BLOG PAGE */
  .blog-page { padding: 80px 56px; }
  .blog-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  .blog-card { border: 1px solid var(--border); border-radius: 16px; overflow: hidden; transition: all 0.25s; cursor: pointer; }
  .blog-card:hover { box-shadow: 0 8px 32px rgba(0,0,0,0.1); transform: translateY(-2px); }
  .blog-card-top { background: var(--ink); padding: 28px; height: 120px; display: flex; align-items: flex-end; position: relative; }
  .blog-cat-badge { font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold); }
  .blog-card-body { padding: 20px; background: white; }
  .blog-title { font-size: 15px; font-weight: 700; color: var(--ink); margin-bottom: 8px; line-height: 1.45; }
  .blog-excerpt { font-size: 13px; color: var(--muted); line-height: 1.7; margin-bottom: 14px; }
  .blog-date { font-size: 11px; color: var(--muted); }
  .blog-post-back { display: flex; align-items: center; gap: 8px; background: none; border: none; cursor: pointer; font-size: 14px; color: var(--muted); padding: 0 0 32px; font-family: 'IBM Plex Sans', sans-serif; transition: color 0.2s; }
  .blog-post-back:hover { color: var(--ink); }
  .blog-post-content { max-width: 720px; }
  .blog-post-title { font-family: 'Playfair Display', serif; font-size: clamp(24px, 3vw, 38px); color: var(--ink); font-weight: 700; line-height: 1.2; margin-bottom: 16px; }
  .blog-post-meta { font-size: 12px; color: var(--muted); margin-bottom: 36px; display: flex; gap: 16px; }
  .blog-post-body { font-size: 15px; color: var(--text); line-height: 1.85; white-space: pre-line; }

  /* COMING SOON MODAL */
  .cs-overlay { position: fixed; inset: 0; z-index: 500; background: rgba(10,20,30,0.72); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: fadeIn 0.2s ease; }
  .cs-box { background: white; border-radius: 20px; max-width: 460px; width: 100%; box-shadow: 0 32px 80px rgba(0,0,0,0.25); overflow: hidden; animation: slideUp 0.28s ease; }
  .cs-top { background: var(--ink); padding: 36px 36px 32px; text-align: center; position: relative; }
  .cs-top::after { content: ''; position: absolute; bottom: 0; left: 24px; right: 24px; height: 1px; background: linear-gradient(90deg, transparent, var(--gold), transparent); }
  .cs-icon { width: 56px; height: 56px; border-radius: 16px; margin: 0 auto 16px; background: var(--gold-p); border: 1px solid var(--gold-b); display: flex; align-items: center; justify-content: center; }
  .cs-x { position: absolute; top: 16px; right: 16px; background: rgba(255,255,255,0.08); border: none; border-radius: 8px; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s; }
  .cs-x:hover { background: rgba(255,255,255,0.14); }
  .cs-title { font-family: 'Playfair Display', serif; font-size: 22px; color: white; font-weight: 700; margin-bottom: 8px; }
  .cs-sub { font-size: 13px; color: rgba(255,255,255,0.45); line-height: 1.6; }
  .cs-body { padding: 32px 36px; }
  .cs-features { display: flex; flex-direction: column; gap: 12px; margin-bottom: 28px; }
  .cs-feat { display: flex; align-items: flex-start; gap: 12px; }
  .cs-feat-icon { width: 28px; height: 28px; border-radius: 7px; background: var(--gold-p); border: 1px solid var(--gold-b); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .cs-feat-icon svg { width: 12px; height: 12px; stroke: var(--gold); }
  .cs-feat-title { font-size: 13px; font-weight: 700; color: var(--ink); margin-bottom: 2px; }
  .cs-feat-desc { font-size: 12px; color: var(--muted); line-height: 1.5; }
  .coming-soon-btns { display: flex; gap: 10px; }

  /* DEMO MODAL */
  .modal-overlay { position: fixed; inset: 0; z-index: 500; background: rgba(10,20,30,0.72); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: fadeIn 0.2s ease; }
  .modal-box { background: white; border-radius: 20px; max-width: 520px; width: 100%; max-height: 90vh; overflow-y: auto; box-shadow: 0 32px 80px rgba(0,0,0,0.25); animation: slideUp 0.28s ease; }
  .modal-top { background: var(--ink); padding: 32px 36px; position: relative; }
  .modal-top::after { content: ''; position: absolute; bottom: 0; left: 24px; right: 24px; height: 1px; background: linear-gradient(90deg, transparent, var(--gold), transparent); }
  .modal-title { font-family: 'Playfair Display', serif; font-size: 22px; color: white; font-weight: 700; margin-bottom: 6px; }
  .modal-sub { font-size: 13px; color: rgba(255,255,255,0.45); }
  .modal-x { position: absolute; top: 16px; right: 16px; background: rgba(255,255,255,0.08); border: none; border-radius: 8px; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; }
  .modal-x:hover { background: rgba(255,255,255,0.14); }
  .modal-body { padding: 32px 36px; }
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .form-group { display: flex; flex-direction: column; gap: 6px; }
  .form-group.full { grid-column: 1 / -1; }
  .form-label { font-size: 12px; font-weight: 600; color: var(--ink); letter-spacing: 0.3px; }
  .form-input { padding: 10px 14px; border: 1.5px solid var(--border); border-radius: 8px; font-size: 14px; color: var(--text); background: white; transition: border-color 0.2s; font-family: 'IBM Plex Sans', sans-serif; outline: none; }
  .form-input:focus { border-color: var(--gold); }
  .modal-actions { margin-top: 24px; display: flex; gap: 10px; }
  .btn-submit { flex: 1; background: var(--ink); color: white; padding: 13px; border-radius: 10px; font-size: 14px; font-weight: 700; border: none; cursor: pointer; transition: all 0.2s; }
  .btn-submit:hover:not(:disabled) { background: var(--ink2); }
  .btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-cancel { background: var(--off); color: var(--muted); padding: 13px 20px; border-radius: 10px; font-size: 14px; font-weight: 600; border: 1.5px solid var(--border); cursor: pointer; }
  .success-state { text-align: center; padding: 20px 0; }
  .success-icon { width: 64px; height: 64px; border-radius: 50%; background: rgba(16,185,129,0.12); border: 2px solid rgba(16,185,129,0.3); display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; }

  /* LEGAL */
  .legal-nav { background: var(--ink); padding: 16px 40px; display: flex; align-items: center; gap: 16px; }
  .legal-back { background: none; border: none; color: rgba(255,255,255,0.6); font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; font-family: 'IBM Plex Sans', sans-serif; }
  .legal-back:hover { color: white; }
  .legal-content { max-width: 800px; margin: 0 auto; padding: 48px 40px; }
  .legal-content h1 { font-family: 'Playfair Display', serif; font-size: 32px; color: var(--ink); margin-bottom: 8px; }
  .legal-content .leg-date { font-size: 13px; color: var(--muted); margin-bottom: 40px; }
  .legal-content h2 { font-size: 17px; font-weight: 700; color: var(--ink); margin: 32px 0 12px; }
  .legal-content p { font-size: 14px; color: var(--muted); line-height: 1.8; margin-bottom: 16px; }
  .legal-content ul { padding-left: 20px; margin-bottom: 16px; }
  .legal-content ul li { font-size: 14px; color: var(--muted); line-height: 1.8; margin-bottom: 6px; }

  /* ANIMATIONS */
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

  /* ABOUT */
  .about-hero { background: var(--ink); padding: 120px 56px 80px; position: relative; overflow: hidden; }
  .about-hero::after { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, var(--ink) 0%, var(--gold) 50%, var(--ink) 100%); }
  .about-hero-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr 380px; gap: 80px; align-items: center; }
  .about-h1 { font-family: 'Playfair Display', serif; font-size: clamp(32px, 4vw, 52px); color: white; font-weight: 700; line-height: 1.15; margin-bottom: 20px; }
  .about-lead { font-size: 16px; color: rgba(255,255,255,0.5); line-height: 1.8; font-weight: 300; max-width: 520px; }
  .origin-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 20px; padding: 32px; }
  .origin-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold); margin-bottom: 14px; }
  .origin-text { font-size: 13px; color: rgba(255,255,255,0.5); line-height: 1.8; }
  .about-story { padding: 96px 56px; background: white; }
  .about-story-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: start; }
  .story-text p { font-size: 15px; color: var(--muted); line-height: 1.85; margin-bottom: 20px; font-weight: 300; }
  .about-values { padding: 96px 56px; background: var(--off); }
  .about-values-inner { max-width: 1200px; margin: 0 auto; }
  .values-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  .value-card { background: white; border: 1px solid var(--border); border-radius: 16px; padding: 28px; }
  .value-icon { width: 44px; height: 44px; border-radius: 11px; background: var(--gold-p); border: 1px solid var(--gold-b); display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
  .value-icon svg { width: 20px; height: 20px; stroke: var(--gold); }
  .value-title { font-size: 15px; font-weight: 700; color: var(--ink); margin-bottom: 8px; }
  .value-desc { font-size: 13px; color: var(--muted); line-height: 1.7; }
  .about-built { padding: 96px 56px; background: white; }
  .about-built-inner { max-width: 1200px; margin: 0 auto; }
  .built-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
  .built-item { background: var(--off); border: 1px solid var(--border); border-radius: 12px; padding: 18px 16px; display: flex; align-items: center; gap: 12px; }
  .built-icon { width: 34px; height: 34px; border-radius: 8px; background: white; border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .built-icon svg { width: 16px; height: 16px; }
  .built-name { font-size: 12px; font-weight: 600; color: var(--ink); }
  .about-team { padding: 96px 56px; background: var(--off); }
  .about-team-inner { max-width: 1200px; margin: 0 auto; }
  .about-cta { background: var(--ink); padding: 96px 56px; text-align: center; }
  .about-cta .sec-title { color: white; }
  .about-cta .sec-sub { color: rgba(255,255,255,0.4); margin: 0 auto 32px; max-width: 560px; }
  .about-cta .eyebrow { color: var(--gold); }
  .about-cta-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

  /* DOWNLOAD STRIP */
  .dl-strip { background: white; border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); padding: 24px 56px; }
  .dl-strip-inner { max-width: 1200px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 24px; flex-wrap: wrap; }
  .dl-strip-left { display: flex; align-items: center; gap: 14px; }
  .dl-strip-icon { width: 42px; height: 42px; border-radius: 10px; background: var(--ink); border: 1px solid rgba(200,151,42,0.35); display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 10px rgba(200,151,42,0.15); }
  .dl-strip-icon svg { width: 18px; height: 18px; stroke: var(--gold); fill: none; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
  .dl-strip-label { font-size: 10.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--muted); margin-bottom: 2px; }
  .dl-strip-title { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 700; color: var(--ink); }
  .dl-strip-btns { display: flex; gap: 10px; align-items: center; }
  .btn-dl-dark { display: inline-flex; align-items: center; gap: 8px; background: var(--ink); color: white; padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 700; border: none; cursor: pointer; transition: all 0.2s; text-decoration: none; font-family: 'IBM Plex Sans', sans-serif; }
  .btn-dl-dark:hover { background: var(--ink2); transform: translateY(-1px); box-shadow: 0 6px 20px rgba(15,25,35,0.18); }
  .btn-dl-dark svg { width: 15px; height: 15px; fill: var(--gold); flex-shrink: 0; }
  .btn-dl-outline { display: inline-flex; align-items: center; gap: 8px; background: white; color: var(--ink); padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 600; border: 1.5px solid var(--border); cursor: pointer; transition: all 0.2s; text-decoration: none; font-family: 'IBM Plex Sans', sans-serif; }
  .btn-dl-outline:hover { border-color: var(--gold); color: var(--gold); }
  .btn-dl-outline svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

  /* DOWNLOAD SECTION */
  .dl-section { background: var(--ink); padding: 80px 56px; }
  .dl-section-inner { max-width: 1200px; margin: 0 auto; }
  .dl-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; max-width: 760px; }
  .dl-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 20px; overflow: hidden; transition: all 0.25s; }
  .dl-card:hover { background: rgba(255,255,255,0.07); transform: translateY(-2px); }
  .dl-card-top { padding: 28px 28px 20px; }
  .dl-card-platform { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
  .dl-platform-icon { width: 44px; height: 44px; border-radius: 11px; background: rgba(200,151,42,0.12); border: 1px solid rgba(200,151,42,0.3); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .dl-platform-icon svg { width: 21px; height: 21px; }
  .dl-platform-name { font-size: 16px; font-weight: 700; color: white; margin-bottom: 2px; }
  .dl-platform-type { font-size: 11px; color: rgba(255,255,255,0.35); }
  .dl-status-live { display: inline-flex; align-items: center; gap: 5px; background: rgba(16,185,129,0.12); color: #34d399; border: 1px solid rgba(16,185,129,0.25); border-radius: 100px; padding: 3px 10px; font-size: 10px; font-weight: 700; margin-left: auto; }
  .dl-status-web { display: inline-flex; align-items: center; gap: 5px; background: rgba(59,130,246,0.12); color: #60a5fa; border: 1px solid rgba(59,130,246,0.25); border-radius: 100px; padding: 3px 10px; font-size: 10px; font-weight: 700; margin-left: auto; }
  .dl-status-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
  .dl-card-desc { font-size: 13px; color: rgba(255,255,255,0.45); line-height: 1.7; margin-bottom: 18px; }
  .dl-highlights { display: flex; flex-direction: column; gap: 7px; }
  .dl-hl { display: flex; align-items: center; gap: 9px; font-size: 12.5px; color: rgba(255,255,255,0.55); }
  .dl-hl-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); flex-shrink: 0; }
  .dl-card-bottom { padding: 16px 28px; border-top: 1px solid rgba(255,255,255,0.07); background: rgba(0,0,0,0.15); display: flex; align-items: center; justify-content: flex-end; }
  .btn-dl-gold { display: inline-flex; align-items: center; gap: 8px; background: var(--gold); color: var(--ink); padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 700; border: none; cursor: pointer; transition: all 0.2s; text-decoration: none; font-family: 'IBM Plex Sans', sans-serif; }
  .btn-dl-gold:hover { background: var(--gold-l); }
  .btn-dl-gold svg { width: 14px; height: 14px; fill: var(--ink); flex-shrink: 0; }
  .btn-dl-white { display: inline-flex; align-items: center; gap: 8px; background: white; color: var(--ink); padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 700; border: none; cursor: pointer; transition: all 0.2s; text-decoration: none; font-family: 'IBM Plex Sans', sans-serif; }
  .btn-dl-white:hover { background: #f0f4f8; }
  .btn-dl-white svg { width: 14px; height: 14px; stroke: var(--ink); fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

  /* RESPONSIVE */
  @media (max-width: 768px) {
    .fj-nav { padding: 0 20px; }
    .fj-navlinks { display: none; }
    .fj-navcta { display: none; }
    .fj-hamburger { display: flex; }
    .fj-mobile-menu { display: flex; }
    .hero { padding: 88px 20px 52px; }
    .hero-inner { grid-template-columns: 1fr; gap: 36px; }
    .hero-cards { display: none; }
    .hero-btns { flex-direction: column; align-items: stretch; }
    .hero-btns button { width: 100%; text-align: center; }
    .trust-strip { padding: 14px 20px; overflow-x: auto; }
    .trust-inner { flex-wrap: nowrap; gap: 20px; min-width: max-content; }
    .features-section { padding: 52px 20px; }
    .feat-grid { grid-template-columns: 1fr 1fr; }
    .pricing-section { padding: 52px 20px; }
    .plans-grid { grid-template-columns: 1fr; gap: 16px; }
    .prefooter { padding: 52px 20px 40px; }
    .prefooter-inner { grid-template-columns: 1fr 1fr; gap: 32px; }
    .fj-footer { padding: 20px; }
    .cta-section { padding: 52px 20px; }
    .cta-btns { flex-direction: column; align-items: stretch; }
    .page-hero { padding: 88px 20px 52px; }
    .features-page, .platforms-page, .integrations-page, .templates-page, .faq-page, .blog-page { padding: 52px 20px; }
    .feat-page-grid { grid-template-columns: 1fr; }
    .platforms-grid { grid-template-columns: 1fr; }
    .integrations-grid { grid-template-columns: 1fr 1fr; }
    .templates-grid { grid-template-columns: 1fr; }
    .blog-grid { grid-template-columns: 1fr; }
    .about-hero { padding: 88px 20px 52px; }
    .about-hero-inner { grid-template-columns: 1fr; gap: 36px; }
    .origin-card { display: none; }
    .about-story { padding: 52px 20px; }
    .about-story-inner { grid-template-columns: 1fr; gap: 36px; }
    .about-values { padding: 52px 20px; }
    .values-grid { grid-template-columns: 1fr; gap: 14px; }
    .about-built { padding: 52px 20px; }
    .built-grid { grid-template-columns: repeat(2, 1fr); }
    .about-team { padding: 52px 20px; }
    .about-cta { padding: 52px 20px; }
    .about-cta-btns { flex-direction: column; align-items: stretch; }
    .dl-strip { padding: 18px 20px; }
    .dl-strip-inner { flex-direction: column; align-items: flex-start; gap: 16px; }
    .dl-strip-btns { width: 100%; }
    .btn-dl-dark, .btn-dl-outline { flex: 1; justify-content: center; }
    .dl-section { padding: 52px 20px; }
    .dl-cards { grid-template-columns: 1fr; max-width: 100%; }
    .coming-soon-btns { flex-direction: column; }
    .legal-nav { padding: 14px 20px; }
    .legal-content { padding: 28px 20px; }
  }
  @media (max-width: 480px) {
    .hero h1 { font-size: 32px; }
    .sec-title { font-size: 26px; }
    .about-h1 { font-size: 30px; }
    .feat-grid { grid-template-columns: 1fr; }
    .built-grid { grid-template-columns: 1fr; }
    .integrations-grid { grid-template-columns: 1fr; }
    .prefooter-inner { grid-template-columns: 1fr; }
  }
`;

// ─── SVG ICONS ────────────────────────────────────────────────────────────────
const FJMark = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: size, height: size }}>
    <line x1="4" y1="5" x2="4" y2="14" />
    <line x1="4" y1="5" x2="9" y2="5" />
    <line x1="4" y1="9.5" x2="8" y2="9.5" />
    <line x1="13" y1="5" x2="19" y2="5" />
    <line x1="16" y1="5" x2="16" y2="16" />
    <path d="M16 16 Q16 19 13 19 Q11 19 11 17" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 9, height: 9, stroke: "inherit" }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── LEGAL PAGES ──────────────────────────────────────────────────────────────
function LegalPage({ title, onBack, children }) {
  return (
    <div>
      <div className="legal-nav">
        <button className="legal-back" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, stroke: "currentColor" }}><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
          Back
        </button>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 13 }}>{title}</span>
      </div>
      <div className="legal-content">{children}</div>
    </div>
  );
}
const LS = ({ children }) => <p>{children}</p>;
const P = ({ children }) => <p>{children}</p>;
const Ul = ({ items }) => <ul>{items.map((i, x) => <li key={x}>{i}</li>)}</ul>;

function TermsPage({ onBack }) {
  return (
    <LegalPage title="Terms of Use" onBack={onBack}>
      <h1>Terms of Use</h1>
      <p className="leg-date">Last updated: January 2026</p>
      <h2>1. Acceptance of Terms</h2>
      <P>By accessing or using FieldJob, you agree to be bound by these Terms of Use. If you do not agree, do not use the platform.</P>
      <h2>2. Use of the Platform</h2>
      <P>FieldJob is a field service management platform for South African contractors and businesses. You may use it only for lawful business purposes.</P>
      <h2>3. Account Responsibility</h2>
      <P>You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.</P>
      <h2>4. Data Ownership</h2>
      <P>You own your data. FieldJob does not sell or share your business data with third parties. You may export your data at any time.</P>
      <h2>5. Payment & Subscriptions</h2>
      <P>Paid plans are billed monthly. You may cancel at any time. No refunds are provided for partial months.</P>
      <h2>6. Limitation of Liability</h2>
      <P>FieldJob is provided as-is. Diswayne Technologies is not liable for any loss of business, data loss or indirect damages arising from use of the platform.</P>
      <h2>7. Contact</h2>
      <P>For queries, contact us at support@fieldjob.getdiswayne.com</P>
    </LegalPage>
  );
}

function PrivacyPage({ onBack }) {
  return (
    <LegalPage title="Privacy Policy" onBack={onBack}>
      <h1>Privacy Policy</h1>
      <p className="leg-date">Last updated: January 2026</p>
      <h2>1. Information We Collect</h2>
      <P>We collect the information you provide when registering — business name, contact details, and the business data you enter into the platform (jobs, clients, invoices, etc.).</P>
      <h2>2. How We Use Your Information</h2>
      <Ul items={["To provide and operate the FieldJob platform", "To send important account and service notifications", "To improve the platform based on usage patterns"]} />
      <h2>3. Data Storage</h2>
      <P>All data is stored in a secure Supabase PostgreSQL database. Data is encrypted in transit using TLS and at rest using AES-256 encryption.</P>
      <h2>4. Data Sharing</h2>
      <P>We do not sell your data. We do not share your data with third parties except where required by South African law.</P>
      <h2>5. Your Rights (POPIA)</h2>
      <P>Under the Protection of Personal Information Act (POPIA), you have the right to access, correct and request deletion of your personal information.</P>
      <h2>6. Cookies</h2>
      <P>The marketing website uses no tracking cookies. The app uses a local session store (IndexedDB) to maintain your login state.</P>
      <h2>7. Contact</h2>
      <P>For privacy queries, contact privacy@fieldjob.getdiswayne.com</P>
    </LegalPage>
  );
}

function CancellationPage({ onBack }) {
  return (
    <LegalPage title="Cancellation Policy" onBack={onBack}>
      <h1>Cancellation Policy</h1>
      <p className="leg-date">Last updated: January 2026</p>
      <h2>Cancelling Your Subscription</h2>
      <P>You can cancel your FieldJob subscription at any time from your account settings. There are no cancellation fees.</P>
      <h2>What Happens After Cancellation</h2>
      <Ul items={["Your account moves to the Free Forever plan immediately", "Your data is retained for 30 days", "You can export all your data during this 30-day period", "After 30 days, your data is permanently deleted"]} />
      <h2>Refunds</h2>
      <P>We do not offer refunds for partial billing periods. Your access continues until the end of the current billing cycle.</P>
      <h2>Reactivation</h2>
      <P>You can reactivate a cancelled subscription at any time. If reactivated within 30 days, your data will be restored.</P>
      <h2>Contact</h2>
      <P>For cancellation assistance, contact support@fieldjob.getdiswayne.com</P>
    </LegalPage>
  );
}

// ─── SHARED NAV ───────────────────────────────────────────────────────────────
function Nav({ activePage, setActivePage, onGetStarted }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);
  const goTo = (page) => { setActivePage(page); closeMenu(); window.scrollTo({ top: 0 }); };
  const goToSection = (page, id) => {
    setActivePage(page); closeMenu();
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 80);
  };
  return (
    <>
      <nav className="fj-nav">
        <button className="fj-logo" onClick={() => goTo("landing")}>
          <div className="fj-logomark"><FJMark size={20} /></div>
          <span className="fj-logotext">FieldJob</span>
        </button>
        <div className="fj-navlinks">
          <button className={activePage === "landing" ? "active" : ""} onClick={() => goTo("landing")}>Home</button>
          <button className={activePage === "features" ? "active" : ""} onClick={() => goTo("features")}>Features</button>
          <button onClick={() => goToSection("landing", "pricing")}>Pricing</button>
          <button onClick={() => goToSection("landing", "download")}>Download</button>
          <button className={activePage === "platforms" ? "active" : ""} onClick={() => goTo("platforms")}>Platforms</button>
          <button className={activePage === "templates" ? "active" : ""} onClick={() => goTo("templates")}>Templates</button>
          <button className={activePage === "blog" ? "active" : ""} onClick={() => goTo("blog")}>Blog</button>
          <button className={activePage === "guide" ? "active" : ""} onClick={() => goTo("guide")}>Instructions</button>
        </div>
        <div className="fj-navcta">
          <button className="btn-ghost" onClick={() => window.open(APP_URL, "_blank")}>Log In</button>
          <button className="btn-fill" onClick={onGetStarted}>Get Started</button>
        </div>
        <button className="fj-hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
          <span style={{ transform: menuOpen ? "rotate(45deg) translate(5px, 5px)" : "none" }} />
          <span style={{ opacity: menuOpen ? 0 : 1 }} />
          <span style={{ transform: menuOpen ? "rotate(-45deg) translate(5px, -5px)" : "none" }} />
        </button>
      </nav>
      <div className={`fj-mobile-menu${menuOpen ? " open" : ""}`}>
        <button onClick={() => goTo("landing")}>Home</button>
        <button onClick={() => goTo("features")}>Features</button>
        <button onClick={() => goToSection("landing", "pricing")}>Pricing</button>
        <button onClick={() => goToSection("landing", "download")}>Download</button>
        <button onClick={() => goTo("platforms")}>Platforms</button>
        <button onClick={() => goTo("templates")}>Templates</button>
        <button onClick={() => goTo("blog")}>Blog</button>
        <button onClick={() => goTo("about")}>About Us</button>
        <button onClick={() => goTo("guide")}>Instructions</button>
        <button onClick={() => goTo("faq")}>FAQ</button>
        <button onClick={() => { window.open(APP_URL, "_blank"); closeMenu(); }}>Log In to App</button>
        <button className="mob-cta" onClick={() => { onGetStarted(); closeMenu(); }}>Get Started →</button>
      </div>
    </>
  );
}

// ─── PRE-FOOTER ───────────────────────────────────────────────────────────────
function PreFooter({ setActivePage, setLegalPage, onGetStarted, content }) {
  const cols = content?.columns || [
    { heading: "Product", links: [{ label: "Features", page: "features" }, { label: "Pricing", page: "landing", section: "pricing" }, { label: "Platforms", page: "platforms" }, { label: "Templates", page: "templates" }, { label: "Integrations", page: "integrations" }] },
    { heading: "Company", links: [{ label: "About us", page: "about" }, { label: "Blog", page: "blog" }, { label: "FAQ", page: "faq" }] },
    { heading: "Legal", links: [{ label: "Terms of use", legal: "terms" }, { label: "Privacy policy", legal: "privacy" }, { label: "Cancellation policy", legal: "cancellation" }] },
  ];
  const handleLink = (link) => {
    if (link.legal) { setLegalPage(link.legal); return; }
    if (link.section) {
      setActivePage(link.page);
      setTimeout(() => document.getElementById(link.section)?.scrollIntoView({ behavior: "smooth" }), 80);
      return;
    }
    setActivePage(link.page);
    window.scrollTo({ top: 0 });
  };
  return (
    <div className="prefooter">
      <div className="prefooter-inner">
        <div className="prefooter-brand">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="fj-logomark" style={{ width: 30, height: 30 }}><FJMark size={16} /></div>
            <span className="fj-logotext">FieldJob</span>
          </div>
          <p className="prefooter-tagline">Built for South African contractors. Manage jobs, quotes, invoices, workers and cashflow in one place.</p>
          <button className="btn-fill" onClick={onGetStarted} style={{ fontSize: 13 }}>Get started free →</button>
        </div>
        {cols.map((col, i) => (
          <div key={i}>
            <div className="prefooter-col-title">{col.heading}</div>
            <div className="prefooter-links">
              {col.links.map((link, j) => (
                <button key={j} onClick={() => handleLink(link)}>{link.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SHARED FOOTER ────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="fj-footer">
      <div className="footer-inner">
        <div className="footer-logo">
          <div className="f-mark"><FJMark size={16} /></div>
          <div>
            <div className="f-name">FieldJob</div>
          </div>
        </div>
        <div className="f-copy">© {new Date().getFullYear()} Diswayne Technologies · South Africa</div>
      </div>
    </footer>
  );
}

// ─── COMING SOON MODAL ────────────────────────────────────────────────────────
function ComingSoonModal({ onClose, onRequestDemo }) {
  return (
    <div className="cs-overlay" onClick={onClose}>
      <div className="cs-box" onClick={e => e.stopPropagation()}>
        <div className="cs-top">
          <button className="cs-x" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, stroke: "rgba(255,255,255,0.6)" }}>
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div className="cs-icon"><FJMark size={24} /></div>
          <div className="cs-title">FieldJob is in early access</div>
          <div className="cs-sub">We are onboarding South African contractors now. Request your demo and we'll get you set up.</div>
        </div>
        <div className="cs-body">
          <div className="cs-features">
            {[
              { title: "Full platform access", desc: "Every module unlocked for 7 days — no card needed.", d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" },
              { title: "Personalised onboarding", desc: "We set up your business profile and walk you through the platform.", d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" },
              { title: "South African pricing", desc: "Pay in Rands. No USD conversion. Cancel anytime.", d: "M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" },
            ].map((f, i) => (
              <div key={i} className="cs-feat">
                <div className="cs-feat-icon">
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12, stroke: "var(--gold)" }}>
                    <path d={f.d} />
                  </svg>
                </div>
                <div><div className="cs-feat-title">{f.title}</div><div className="cs-feat-desc">{f.desc}</div></div>
              </div>
            ))}
          </div>
          <div className="coming-soon-btns">
            <button className="btn-submit" onClick={() => { onClose(); onRequestDemo(); }}>Request Demo Access</button>
            <button className="btn-cancel" onClick={onClose}>Maybe later</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DEMO MODAL ───────────────────────────────────────────────────────────────
function DemoModal({ onClose, formData, setFormData, submitted, submitting, onSubmit }) {
  const set = (k, v) => setFormData(p => ({ ...p, [k]: v }));
  if (submitted) return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-top">
          <button className="modal-x" onClick={onClose}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, stroke: "rgba(255,255,255,0.6)" }}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
          <div className="modal-title">Demo Request Sent</div>
        </div>
        <div className="modal-body">
          <div className="success-state">
            <div className="success-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 28, height: 28, stroke: "var(--emerald)" }}><polyline points="20 6 9 17 4 12" /></svg></div>
            <div className="sec-title" style={{ fontSize: 22 }}>You're on the list</div>
            <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 10, lineHeight: 1.7 }}>We'll review your request and send your access details within 24 hours. Check your email.</p>
            <button className="btn-primary" style={{ marginTop: 24 }} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    </div>
  );
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-top">
          <button className="modal-x" onClick={onClose}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, stroke: "rgba(255,255,255,0.6)" }}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
          <div className="modal-title">Request Demo Access</div>
          <div className="modal-sub">Fill in your details and we'll get you set up within 24 hours.</div>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Business Name *</label><input className="form-input" placeholder="e.g. Bosman Electrical" value={formData.business_name} onChange={e => set("business_name", e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Your Name</label><input className="form-input" placeholder="Full name" value={formData.owner_name} onChange={e => set("owner_name", e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Email Address *</label><input className="form-input" type="email" placeholder="you@business.co.za" value={formData.email} onChange={e => set("email", e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Phone Number *</label><input className="form-input" placeholder="071 000 0000" value={formData.phone} onChange={e => set("phone", e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Industry</label>
              <select className="form-input" value={formData.industry} onChange={e => set("industry", e.target.value)}>
                <option value="">Select industry…</option>
                {["Electrical","Plumbing","HVAC / Refrigeration","Building & Construction","Painting & Renovations","Security","Cleaning","General Contracting","Other"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Team Size</label>
              <select className="form-input" value={formData.workers} onChange={e => set("workers", e.target.value)}>
                <option value="">Select…</option>
                {["Just me","2–5 workers","6–10 workers","11–25 workers","25+ workers"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-submit" onClick={onSubmit} disabled={submitting || !formData.business_name || !formData.email || !formData.phone}>{submitting ? "Sending…" : "Request Demo Access"}</button>
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CUSTOM BUILD MODAL ───────────────────────────────────────────────────────
function CustomModal({ onClose, cbForm, setCbForm, cbSubmitted, cbSubmitting, onSubmit }) {
  const set = (k, v) => setCbForm(p => ({ ...p, [k]: v }));
  if (cbSubmitted) return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-top"><button className="modal-x" onClick={onClose}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, stroke: "rgba(255,255,255,0.6)" }}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
          <div className="modal-title">Request Received</div></div>
        <div className="modal-body"><div className="success-state">
          <div className="success-icon"><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 28, height: 28, stroke: "var(--emerald)" }}><polyline points="20 6 9 17 4 12" /></svg></div>
          <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 10, lineHeight: 1.7 }}>We'll be in touch within 2 business days.</p>
          <button className="btn-primary" style={{ marginTop: 24 }} onClick={onClose}>Done</button>
        </div></div>
      </div>
    </div>
  );
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-top"><button className="modal-x" onClick={onClose}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, stroke: "rgba(255,255,255,0.6)" }}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
          <div className="modal-title">Request a Custom Build</div>
          <div className="modal-sub">Tell us what you need and we'll propose a solution.</div>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Business Name</label><input className="form-input" placeholder="Company name" value={cbForm.business_name} onChange={e => set("business_name", e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Email Address</label><input className="form-input" type="email" placeholder="you@company.co.za" value={cbForm.email} onChange={e => set("email", e.target.value)} /></div>
            <div className="form-group full"><label className="form-label">What do you need?</label><textarea className="form-input" rows={4} placeholder="Describe your requirements…" value={cbForm.description} onChange={e => set("description", e.target.value)} style={{ resize: "vertical" }} /></div>
          </div>
          <div className="modal-actions">
            <button className="btn-submit" onClick={onSubmit} disabled={cbSubmitting}>{cbSubmitting ? "Sending…" : "Send Request"}</button>
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ICON PATHS MAP ──────────────────────────────────────────────────────────
const ICON_PATHS = {
  jobs:       { d: "M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" },
  invoice:    { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", poly: "14 2 14 8 20 8" },
  workers:    { d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2", circle: { cx: 9, cy: 7, r: 4 } },
  inventory:  { d: "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" },
  expenses:   { rect: { x: 1, y: 4, width: 22, height: 16, rx: 2 }, line: { x1: 1, y1: 10, x2: 23, y2: 10 } },
  compliance: { d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" },
  cashflow:   { poly: "22 12 18 12 15 21 9 3 6 12 2 12" },
  whatsapp:   { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" },
  portal:     { d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2", circle: { cx: 9, cy: 7, r: 4 } },
  report:     { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", poly: "14 2 14 8 20 8" },
  dashboard:  { d: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" },
  fuel:       { d: "M3 22V8l9-6 9 6v14M9 22V12h6v10" },
  guide:      { d: "M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" },
};
function FeatureIcon({ icon, size = 18, stroke = "var(--ink)" }) {
  const p = ICON_PATHS[icon] || ICON_PATHS.jobs;
  return (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: size, height: size, stroke }}>
      {p.d && <path d={p.d} />}
      {p.poly && <polyline points={p.poly} />}
      {p.rect && <rect {...p.rect} />}
      {p.circle && <circle {...p.circle} />}
      {p.line && <line {...p.line} />}
    </svg>
  );
}

// ─── LANDING PAGE ─────────────────────────────────────────────────────────────
function LandingPage({ onGetStarted, onRequestDemo, cms }) {
  const hero = cms.hero || {};
  const pricingCms = cms.pricing || {};
  const ctaCms = cms.cta || {};
  const featuresCms = cms.features || {};

  const features = featuresCms.modules || [
    { title: "Job Management", desc: "Create, assign and track every callout from start to completion. Never lose a job again.", d: "M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" },
    { title: "Quotes & Invoices", desc: "Branded PDF documents with VAT, sent directly via WhatsApp or email.", d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", poly: "14 2 14 8 20 8" },
    { title: "Worker Tracking", desc: "Assign jobs, track performance and manage your entire field team.", d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2", circle: { cx: 9, cy: 7, r: 4 } },
    { title: "Inventory Control", desc: "Track stock and materials with low-stock alerts.", d: "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" },
    { title: "Expense Tracking", desc: "Log costs and see your true profit margins per job.", rect: { x: 1, y: 4, width: 22, height: 16, rx: 2 }, line: { x1: 1, y1: 10, x2: 23, y2: 10 } },
    { title: "Compliance Docs", desc: "COCs and certificates stored and ready for inspection.", d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" },
    { title: "Cashflow Reports", desc: "Real-time revenue and business health at a glance.", poly: "22 12 18 12 15 21 9 3 6 12 2 12" },
    { title: "WhatsApp Ready", desc: "Share quotes and job updates via WhatsApp in one click.", d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" },
  ];

  const plans = pricingCms.plans || [
    { id:"free", name:"Free Forever", price:"0", period:"no credit card needed", highlight:false, badge:"", features:["1 user","3 active jobs","Basic invoicing"], cta:"Get started free", cta_style:"outline", note:"Always free" },
    { id:"starter", name:"Starter", price:"79", period:"per month", highlight:false, badge:"", features:["3 users","Unlimited jobs","Quotes & invoices","Expense tracking"], cta:"Start free trial", cta_style:"outline", note:"7-day free trial" },
    { id:"business", name:"Business", price:"169", period:"per month", highlight:true, badge:"Most popular", features:["Unlimited users","All 12 modules","Client portal","Priority support"], cta:"Get Business", cta_style:"gold", note:"7-day free trial" },
    { id:"enterprise", name:"Custom Enterprise", price:"Custom", period:"tailored to you", highlight:false, badge:"", features:["Multi-branch","White-label","Custom modules","SLA support"], cta:"Contact us", cta_style:"outline", note:"Talk to us" },
  ];

  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="hero-inner">
          <div style={{ position: "relative", zIndex: 1 }}>
            <div className="hero-tag">{hero.tag || "South African Field Service Platform"}</div>
            <h1>Run your contracting<br />business <em>{hero.headline_em || "properly."}</em></h1>
            <p className="hero-sub">{hero.subtext || "Jobs, quotes, invoices, workers, stock and cashflow — all connected in one platform."}</p>
            <div className="hero-btns">
              <button className="btn-primary" onClick={onGetStarted}>{hero.cta_primary || "Get Started"}</button>
              <button className="btn-secondary" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}>{hero.cta_secondary || "View features"} →</button>
            </div>
          </div>
          <div className="hero-cards" style={{ position: "relative", zIndex: 1 }}>
            {[
              { title: "Revenue this month", sub: "3 invoices paid", val: "R48,200", valColor: "var(--emerald)", iconD: "M22 12h-4l-3 9L9 3l-3 9H2" },
              { title: "Jobs this week", sub: "2 completed · 3 scheduled", val: "5", valColor: "var(--gold)", iconD: "M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" },
              { title: "Quotes pending", sub: "Sent to 4 clients", val: "R22,500", valColor: "#3b82f6", iconD: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", iconPoly: "14 2 14 8 20 8" },
              { title: "Compliance", sub: "All COCs up to date", val: "✓ Healthy", valColor: "var(--emerald)", valSize: 13, iconD: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z", border: true },
            ].map((c, i) => (
              <div key={i} className="hc" style={c.border ? { borderColor: "var(--gold-b)", background: "rgba(200,151,42,0.04)" } : {}}>
                <div className="hc-icon">
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18, stroke: "var(--gold)", position: "relative", zIndex: 1 }}>
                    {c.iconD && <path d={c.iconD} />}
                    {c.iconPoly && <polyline points={c.iconPoly} />}
                  </svg>
                </div>
                <div><div className="hc-title">{c.title}</div><div className="hc-sub">{c.sub}</div></div>
                <div className="hc-val" style={{ color: c.valColor, fontSize: c.valSize || 18, fontWeight: 700 }}>{c.val}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOWNLOAD STRIP */}
      <div className="dl-strip">
        <div className="dl-strip-inner">
          <div className="dl-strip-left">
            <div className="dl-strip-icon">
              <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="none"/><path d="M8 12h8M12 8v8" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round"/><circle cx="12" cy="12" r="10" fill="none" stroke="rgba(200,151,42,0.3)" strokeWidth="1"/><path d="M8 17l4-4 4 4M12 13V5" fill="none" stroke="var(--gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div>
              <div className="dl-strip-label">Available now</div>
              <div className="dl-strip-title">Get the FieldJob App</div>
            </div>
          </div>
          <div className="dl-strip-btns">
            <a className="btn-dl-dark" href="https://htpedzjlwfcglwelgofu.supabase.co/storage/v1/object/public/downloads/FieldJob.apk" download>
              <svg viewBox="0 0 24 24"><path d="M17.523 15.341l-.9-1.56A5.98 5.98 0 0015 6.07l1.5-2.6-1.73-1-1.5 2.6a5.95 5.95 0 00-2.54 0L9.23 2.47l-1.73 1L9 6.07a5.98 5.98 0 00-1.623 7.711l-.9 1.56 1.73 1 .9-1.56a5.95 5.95 0 005.786 0l.9 1.56 1.73-1z"/></svg>
              Download for Android
            </a>
            <a className="btn-dl-outline" href={`${APP_URL}?logout=1`} target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              Open Web App
            </a>
          </div>
        </div>
      </div>

      {/* TRUST STRIP */}
      <div className="trust-strip">
        <div className="trust-inner">
          <div className="trust-label">Built for</div>
          <div className="trust-div"></div>
          <div className="trust-items">
            {[
              { label: "Electricians", d: "M13 2L3 14h9l-1 8 10-12h-9l1-8z" },
              { label: "Plumbers", d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" },
              { label: "HVAC & Refrigeration", rect: { x: 2, y: 3, width: 20, height: 14, rx: 2 } },
              { label: "Builders & Contractors", d: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" },
              { label: "Painters & Renovators", rect2: { x: 1, y: 3, width: 15, height: 13, rx: 2 } },
              { label: "Security Companies", circle: { cx: 12, cy: 12, r: 3 } },
            ].map((t, i) => (
              <div key={i} className="trust-item">
                <div className="trust-icon">
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13, stroke: "#64748b" }}>
                    {t.d && <path d={t.d} />}
                    {t.rect && <rect {...t.rect} />}
                    {t.rect2 && <rect {...t.rect2} />}
                    {t.circle && <circle {...t.circle} />}
                  </svg>
                </div>
                {t.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FEATURES PREVIEW */}
      <section className="features-section" id="features">
        <div className="sec-inner">
          <div className="eyebrow">{featuresCms.eyebrow || "Platform Features"}</div>
          <div className="sec-title">{featuresCms.headline || "12 modules. One subscription. Zero chaos."}</div>
          <p className="sec-sub">{featuresCms.subtext || "Every tool your field service business needs."}</p>
          <div className="feat-grid">
            {features.slice(0, 8).map((f, i) => (
              <div key={i} className="feat">
                <div className="feat-num">{String(i + 1).padStart(2, "0")}</div>
                <div className="feat-icon">
                  <FeatureIcon icon={f.icon || "jobs"} size={18} stroke="var(--ink)" />
                </div>
                <div className="feat-title">{f.title}</div>
                <div className="feat-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING — 4 CARDS */}
      <section className="pricing-section" id="pricing">
        <div className="pricing-top sec-inner">
          <div className="eyebrow">{pricingCms.eyebrow || "Pricing"}</div>
          <div className="sec-title">{pricingCms.headline || "Simple pricing. No surprises."}</div>
          <p className="sec-sub">{pricingCms.subtext || "Start free forever. Upgrade when you are ready."}</p>
        </div>
        <div className="plans-grid">
          {plans.map((plan, i) => (
            <div key={i} className={`plan-card${plan.highlight ? " highlighted" : ""}`}>
              {plan.badge && <div className="plan-badge">{plan.badge}</div>}
              <div className="plan-name">{plan.name}</div>
              <div className="plan-price-row">
                {plan.price !== "Custom" && <span className="plan-cur">R</span>}
                <span className={`plan-amt${plan.price === "Custom" ? " custom" : ""}`}>{plan.price}</span>
              </div>
              <div className="plan-period">{plan.period}</div>
              <div className="plan-divider"></div>
              <div className="plan-feats">
                {(plan.features || []).map((f, j) => (
                  <div key={j} className="pf">
                    <div className={`pf-dot${plan.highlight ? " highlighted" : ""}`}>
                      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: plan.highlight ? "var(--emerald)" : "var(--gold)" }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    {f}
                  </div>
                ))}
              </div>
              <button
                className={`plan-cta ${plan.cta_style || "outline"}`}
                onClick={plan.id === "enterprise" ? onRequestDemo : onGetStarted}
              >
                {plan.cta}
              </button>
              <div className="plan-note">{plan.note}</div>
            </div>
          ))}
        </div>
      </section>

      {/* DOWNLOAD SECTION */}
      <section className="dl-section" id="download">
        <div className="dl-section-inner">
          <div className="eyebrow" style={{ color:"var(--gold)" }}>Get the app</div>
          <div className="sec-title" style={{ color:"white", marginBottom:14 }}>FieldJob on every <em style={{ color:"var(--gold)", fontStyle:"italic" }}>device</em></div>
          <p className="sec-sub" style={{ color:"rgba(255,255,255,0.45)", marginBottom:40 }}>Install FieldJob on your Android phone or access the full platform instantly from any browser — no setup required.</p>
          <div className="dl-cards">

            {/* Android card */}
            <div className="dl-card">
              <div className="dl-card-top">
                <div className="dl-card-platform">
                  <div className="dl-platform-icon">
                    <svg viewBox="0 0 24 24" fill="none" style={{ width:21, height:21 }}>
                      <path d="M17.523 15.341l-.9-1.56A5.98 5.98 0 0015 6.07l1.5-2.6-1.73-1-1.5 2.6a5.95 5.95 0 00-2.54 0L9.23 2.47l-1.73 1L9 6.07a5.98 5.98 0 00-1.623 7.711l-.9 1.56 1.73 1 .9-1.56a5.95 5.95 0 005.786 0l.9 1.56 1.73-1z" fill="#c8972a"/>
                    </svg>
                  </div>
                  <div>
                    <div className="dl-platform-name">Android</div>
                    <div className="dl-platform-type">APK · Direct install</div>
                  </div>
                  <div className="dl-status-live"><span className="dl-status-dot"></span>Live</div>
                </div>
                <div className="dl-card-desc">Install directly on any Android phone or tablet. No Play Store required — download and install in under a minute.</div>
                <div className="dl-highlights">
                  <div className="dl-hl"><span className="dl-hl-dot"></span>Works offline — syncs when back online</div>
                  <div className="dl-hl"><span className="dl-hl-dot"></span>Full platform — all modules included</div>
                  <div className="dl-hl"><span className="dl-hl-dot"></span>Push notifications for bookings & invoices</div>
                </div>
              </div>
              <div className="dl-card-bottom">
                <a className="btn-dl-gold" href="https://htpedzjlwfcglwelgofu.supabase.co/storage/v1/object/public/downloads/FieldJob.apk" download>
                  <svg viewBox="0 0 24 24"><path d="M17.523 15.341l-.9-1.56A5.98 5.98 0 0015 6.07l1.5-2.6-1.73-1-1.5 2.6a5.95 5.95 0 00-2.54 0L9.23 2.47l-1.73 1L9 6.07a5.98 5.98 0 00-1.623 7.711l-.9 1.56 1.73 1 .9-1.56a5.95 5.95 0 005.786 0l.9 1.56 1.73-1z"/></svg>
                  Download APK
                </a>
              </div>
            </div>

            {/* Web App card */}
            <div className="dl-card">
              <div className="dl-card-top">
                <div className="dl-card-platform">
                  <div className="dl-platform-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#c8972a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width:21, height:21 }}>
                      <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="dl-platform-name">Web App</div>
                    <div className="dl-platform-type">Browser · Any device</div>
                  </div>
                  <div className="dl-status-web"><span className="dl-status-dot"></span>Online</div>
                </div>
                <div className="dl-card-desc">Access FieldJob instantly from any browser on desktop, tablet or phone. Nothing to download — just sign in and go.</div>
                <div className="dl-highlights">
                  <div className="dl-hl"><span className="dl-hl-dot"></span>Works on Windows, Mac, iOS & Android</div>
                  <div className="dl-hl"><span className="dl-hl-dot"></span>Install to desktop for a native-like feel</div>
                  <div className="dl-hl"><span className="dl-hl-dot"></span>Always the latest version automatically</div>
                </div>
              </div>
              <div className="dl-card-bottom">
                <a className="btn-dl-white" href={`${APP_URL}?logout=1`} target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width:14, height:14 }}><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  Open App
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-inner">
          <div className="eyebrow">{ctaCms.eyebrow || "Get Started Today"}</div>
          <div className="sec-title" style={{ textAlign: "center" }}>{ctaCms.headline || "Ready to run your business like a professional."}</div>
          <p className="sec-sub" style={{ maxWidth: "100%", textAlign: "center" }}>{ctaCms.subtext || "Join South African contractors who've replaced their spreadsheets and WhatsApp chaos with FieldJob."}</p>
          <div className="cta-btns">
            <button className="btn-gold" onClick={onGetStarted}>{ctaCms.cta_primary || "Get Started"}</button>
            <button className="btn-primary" onClick={() => window.open(APP_URL, "_blank")}>{ctaCms.cta_secondary || "Login to App"} →</button>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── FEATURES PAGE ────────────────────────────────────────────────────────────
function FeaturesPage({ onGetStarted, cms }) {
  const c = cms || {};
  const modules = c.modules || [];
  const iconPaths = {
    jobs: "M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z",
    invoice: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z",
    workers: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2",
    inventory: "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z",
    expenses: "M1 4h22v16H1z",
    compliance: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
    cashflow: "M22 12h-4l-3 9L9 3l-3 9H2",
    whatsapp: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
    portal: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2",
    report: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z",
    dashboard: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
    fuel: "M3 22V8l9-6 9 6v14",
  };
  return (
    <>
      <div className="page-hero">
        <div className="page-hero-inner">
          <div className="eyebrow">{c.eyebrow || "Platform Features"}</div>
          <div className="sec-title">{c.headline || "Everything your business needs."}</div>
          <p className="sec-sub">{c.subtext || "12 modules, all included in every paid plan."}</p>
        </div>
      </div>
      <div className="features-page sec-inner">
        <div className="feat-page-grid">
          {modules.map((m, i) => (
            <div key={i} className="feat-page-card">
              <div className="feat-page-icon">
                <FeatureIcon icon={m.icon || "jobs"} size={22} stroke="var(--gold)" />
              </div>
              <div className="feat-page-title">{m.title}</div>
              <div className="feat-page-desc">{m.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 64, textAlign: "center" }}>
          <div className="eyebrow">Ready to try it?</div>
          <div className="sec-title" style={{ textAlign: "center" }}>All 12 modules. One subscription.</div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 24 }}>
            <button className="btn-primary" onClick={onGetStarted}>Get Started</button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── PLATFORMS PAGE ───────────────────────────────────────────────────────────
function PlatformsPage({ onGetStarted, cms }) {
  const c = cms || {};
  const platforms = c.platforms || [
    { name: "Web App", tagline: "Full power in your browser", desc: "The complete FieldJob platform accessible from any browser.", available: "Available now", available_color: "green", highlights: ["Works on Chrome, Firefox, Safari, Edge", "Offline-capable with service worker", "Full admin and reporting access"] },
    { name: "Mobile App", tagline: "Field-ready on Android & iOS", desc: "Native mobile apps for your workers in the field.", available: "Coming soon", available_color: "amber", highlights: ["Android & iOS", "Offline mode with auto-sync", "Photo capture for site reports"] },
    { name: "Desktop App", tagline: "Windows & macOS power users", desc: "A dedicated desktop application for owners and admins.", available: "Coming soon", available_color: "amber", highlights: ["Windows & macOS", "Auto-updates silently", "Native file system access"] },
  ];
  return (
    <>
      <div className="page-hero">
        <div className="page-hero-inner">
          <div className="eyebrow">{c.eyebrow || "Platforms"}</div>
          <div className="sec-title">{c.headline || "One platform. Every device."}</div>
          <p className="sec-sub">{c.subtext || "FieldJob runs wherever your team works."}</p>
        </div>
      </div>
      <div className="platforms-page sec-inner">
        <div className="platforms-grid">
          {platforms.map((p, i) => (
            <div key={i} className="platform-card">
              <div className="platform-top">
                <div className="platform-name">{p.name}</div>
                <div className="platform-tagline">{p.tagline}</div>
                <div className={`platform-badge ${p.available_color}`}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", display: "inline-block" }}></span>
                  {p.available}
                </div>
              </div>
              <div className="platform-body">
                <p className="platform-desc">{p.desc}</p>
                <div className="platform-highlights">
                  {(p.highlights || []).map((h, j) => (
                    <div key={j} className="platform-hl">
                      <div className="platform-hl-dot"></div>
                      {h}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 64, textAlign: "center" }}>
          <button className="btn-primary" onClick={onGetStarted}>Start on Web App Now</button>
          <p style={{ marginTop: 12, fontSize: 13, color: "var(--muted)" }}>Get notified when mobile & desktop launch — just sign up</p>
        </div>
      </div>
    </>
  );
}

// ─── INTEGRATIONS PAGE ────────────────────────────────────────────────────────
function IntegrationsPage({ cms }) {
  const c = cms || {};
  const integrations = c.integrations || [];
  const categories = [...new Set(integrations.map(i => i.category))];
  return (
    <>
      <div className="page-hero">
        <div className="page-hero-inner">
          <div className="eyebrow">{c.eyebrow || "Integrations"}</div>
          <div className="sec-title">{c.headline || "Connects with the tools you use."}</div>
          <p className="sec-sub">{c.subtext || "FieldJob works with South African payment providers and communication tools."}</p>
        </div>
      </div>
      <div className="integrations-page sec-inner">
        {categories.map(cat => (
          <div key={cat} style={{ marginBottom: 48 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "var(--muted)", marginBottom: 16 }}>{cat}</div>
            <div className="integrations-grid">
              {integrations.filter(i => i.category === cat).map((int, j) => (
                <div key={j} className="integration-card">
                  <div className="int-cat">{int.category}</div>
                  <div className="int-name">{int.name}</div>
                  <div className="int-desc">{int.desc}</div>
                  <span className={`int-badge ${int.status === "Live" ? "live" : "soon"}`}>{int.status}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── TEMPLATES PAGE ───────────────────────────────────────────────────────────
function TemplatesPage({ onGetStarted, cms }) {
  const c = cms || {};
  const templates = c.templates || [];
  return (
    <>
      <div className="page-hero">
        <div className="page-hero-inner">
          <div className="eyebrow">{c.eyebrow || "Templates"}</div>
          <div className="sec-title">{c.headline || "Professional documents, ready to send."}</div>
          <p className="sec-sub">{c.subtext || "Every document FieldJob generates is branded and professional."}</p>
        </div>
      </div>
      <div className="templates-page sec-inner">
        <div className="templates-grid">
          {templates.map((t, i) => (
            <div key={i} className="template-card">
              <div className="template-preview" style={{ background: t.preview_color || "#0f1923" }}>
                <div className="template-preview-inner">
                  <div className="template-preview-line"></div>
                  <div className="template-preview-line short"></div>
                  <div className="template-preview-line tiny"></div>
                  <div className="template-preview-line tiny"></div>
                  <div className="template-preview-line tiny"></div>
                </div>
              </div>
              <div className="template-info">
                <div className="template-cat">{t.category}</div>
                <div className="template-name">{t.name}</div>
                <div className="template-desc">{t.desc}</div>
                {t.file_url && (
                  <a href={t.file_url} target="_blank" rel="noreferrer" download style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 12, padding: "8px 14px", background: "var(--off)", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "var(--ink)", textDecoration: "none", transition: "all 0.2s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--gold)"; e.currentTarget.style.color = "var(--gold)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--ink)"; }}>
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12, stroke: "currentColor" }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download {t.file_type || "template"}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 56, textAlign: "center" }}>
          <button className="btn-primary" onClick={onGetStarted}>Try FieldJob Free</button>
        </div>
      </div>
    </>
  );
}

// ─── FAQ PAGE ─────────────────────────────────────────────────────────────────
function FAQPage({ cms }) {
  const [open, setOpen] = useState(null);
  const c = cms || {};
  const questions = c.questions || [];
  return (
    <>
      <div className="page-hero">
        <div className="page-hero-inner">
          <div className="eyebrow">{c.eyebrow || "FAQ"}</div>
          <div className="sec-title">{c.headline || "Frequently asked questions."}</div>
          <p className="sec-sub">{c.subtext || "Everything you need to know about FieldJob."}</p>
        </div>
      </div>
      <div className="faq-page sec-inner">
        <div className="faq-list">
          {questions.map((item, i) => (
            <div key={i} className="faq-item">
              <div className="faq-q" onClick={() => setOpen(open === i ? null : i)}>
                <div className="faq-q-text">{item.q}</div>
                <div className={`faq-chevron${open === i ? " open" : ""}`}>
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
              {open === i && <div className="faq-a">{item.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── BLOG PAGE ────────────────────────────────────────────────────────────────
function BlogPage({ cms }) {
  const [activePost, setActivePost] = useState(null);
  const c = cms || {};
  const posts = c.posts || [];

  if (activePost) {
    const post = posts.find(p => p.id === activePost);
    if (!post) return null;
    return (
      <div style={{ padding: "80px 56px", maxWidth: 1200, margin: "0 auto" }}>
        <button className="blog-post-back" onClick={() => setActivePost(null)}>
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
          Back to Blog
        </button>
        <div className="blog-post-content">
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "var(--gold)", marginBottom: 12 }}>{post.category}</div>
          {post.image && <img src={post.image} alt={post.title} style={{ width: "100%", borderRadius: 12, marginBottom: 28, maxHeight: 360, objectFit: "cover" }} />}
          <div className="blog-post-title">{post.title}</div>
          <div className="blog-post-meta">
            <span>{new Date(post.date).toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" })}</span>
            <span>FieldJob Team</span>
          </div>
          <div className="blog-post-body">{post.content}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-hero">
        <div className="page-hero-inner">
          <div className="eyebrow">{c.eyebrow || "Blog"}</div>
          <div className="sec-title">{c.headline || "Insights for South African contractors."}</div>
          <p className="sec-sub">{c.subtext || "Practical advice on running a profitable field service business."}</p>
        </div>
      </div>
      <div className="blog-page sec-inner">
        {posts.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "var(--muted)" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✍</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No posts yet</div>
            <div style={{ fontSize: 14 }}>New articles will appear here once published from the admin panel.</div>
          </div>
        ) : (
          <div className="blog-grid">
            {posts.map((post, i) => (
              <div key={i} className="blog-card" onClick={() => setActivePost(post.id)}>
                <div className="blog-card-top" style={post.image ? { backgroundImage: `url(${post.image})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
                  <div className="blog-cat-badge">{post.category}</div>
                </div>
                <div className="blog-card-body">
                  <div className="blog-title">{post.title}</div>
                  <div className="blog-excerpt">{post.excerpt}</div>
                  <div className="blog-date">{new Date(post.date).toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" })}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ─── ABOUT PAGE ───────────────────────────────────────────────────────────────
function AboutPage({ onGetStarted, cms }) {
  const c = cms || {};
  const modules = [
    { name: "Job Management", d: "M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" },
    { name: "Quotes & Invoices", d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", poly: "14 2 14 8 20 8" },
    { name: "Worker Tracking", d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2", circle: { cx: 9, cy: 7, r: 4 } },
    { name: "Inventory Control", d: "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" },
    { name: "Expense Tracking", rect: { x: 1, y: 4, width: 22, height: 16, rx: 2 }, line: { x1: 1, y1: 10, x2: 23, y2: 10 } },
    { name: "Compliance Docs", d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" },
    { name: "Cashflow Reports", poly: "22 12 18 12 15 21 9 3 6 12 2 12" },
    { name: "WhatsApp Sharing", d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" },
    { name: "Client Management", rect: { x: 2, y: 3, width: 20, height: 14, rx: 2 } },
    { name: "Business Dashboard", d: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" },
  ];
  return (
    <>
      <div className="about-hero">
        <div className="about-hero-inner">
          <div>
            <div className="eyebrow" style={{ color: "rgba(200,151,42,0.7)" }}>About FieldJob</div>
            <div className="about-h1">Built by contractors,<br />for contractors.</div>
            <p className="about-lead">FieldJob was built to solve a real problem — South African field service businesses were running on WhatsApp groups, paper job cards and Excel spreadsheets. There had to be a better way.</p>
          </div>
          <div className="origin-card">
            <div className="origin-eyebrow">Our Origin</div>
            <div className="origin-text">FieldJob started as a custom tool for a single electrical contractor in South Africa who was tired of losing money to disorganised admin. Within months, it became clear that every contractor had the same problems.</div>
          </div>
        </div>
      </div>

      <div className="about-story">
        <div className="about-story-inner sec-inner">
          <div className="story-text">
            <div className="eyebrow">The Problem</div>
            <div className="sec-title">South African contractors deserve better tools.</div>
            <p>Most field service software is built for large enterprises in the US or UK. It's expensive, complicated and doesn't account for how South African contractors actually work — cash-heavy, WhatsApp-first, constantly on the move.</p>
            <p>FieldJob was designed from the ground up for the South African market. Local pricing in Rands. SARS-ready expense tracking. WhatsApp integration built in from day one. Compliance documents for South African standards.</p>
          </div>
          <div>
            <div className="eyebrow">What We Built</div>
            <div className="sec-title">One platform. Every module.</div>
            <div className="built-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
              {modules.map((m, i) => (
                <div key={i} className="built-item">
                  <div className="built-icon">
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, stroke: "var(--gold)" }}>
                      {m.d && <path d={m.d} />}
                      {m.poly && <polyline points={m.poly} />}
                      {m.rect && <rect {...m.rect} />}
                      {m.circle && <circle {...m.circle} />}
                      {m.line && <line {...m.line} />}
                    </svg>
                  </div>
                  <div className="built-name">{m.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="about-values">
        <div className="about-values-inner sec-inner">
          <div className="eyebrow">Our Values</div>
          <div className="sec-title">What we stand for.</div>
          <div className="values-grid">
            {[
              { title: "Built for South Africa", desc: "Local pricing, local standards, local payment methods. FieldJob is designed specifically for how South African contractors work.", d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" },
              { title: "Simple over complicated", desc: "If a contractor who's been using paper job cards can't figure it out in 10 minutes, we've failed. Simplicity is a feature.", d: "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" },
              { title: "Your data is yours", desc: "We never sell your data. You can export everything at any time. If you cancel, you keep your data for 30 days.", d: "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" },
            ].map((v, i) => (
              <div key={i} className="value-card">
                <div className="value-icon">
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20, stroke: "var(--gold)" }}>
                    <path d={v.d} />
                  </svg>
                </div>
                <div className="value-title">{v.title}</div>
                <div className="value-desc">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="about-cta">
        <div className="eyebrow">Get Started</div>
        <div className="sec-title">Ready to try FieldJob?</div>
        <p className="sec-sub">7-day free trial. No credit card required. Set up in under 10 minutes.</p>
        <div className="about-cta-btns">
          <button className="btn-gold" onClick={onGetStarted}>Start Free Trial</button>
          <button className="btn-secondary" style={{ background: "transparent", color: "rgba(255,255,255,0.6)", borderColor: "rgba(255,255,255,0.2)" }} onClick={() => window.open(APP_URL, "_blank")}>Login to App →</button>
        </div>
      </div>
    </>
  );
}


// ─── GUIDE PAGE ───────────────────────────────────────────────────────────────
function GuidePage({ cms }) {
  const c = cms || {};
  const sections = c.sections || [];
  return (
    <>
      <div className="page-hero">
        <div className="page-hero-inner">
          <div className="eyebrow">{c.eyebrow || "How to use FieldJob"}</div>
          <div className="sec-title">{c.headline || "Your step-by-step guide."}</div>
          <p className="sec-sub">{c.subtext || "Everything you need to get your business running on FieldJob."}</p>
        </div>
      </div>
      <div style={{ padding: "72px 56px", maxWidth: 1200, margin: "0 auto" }}>
        {sections.length === 0 && (
          <div style={{ textAlign: "center", padding: 48, color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 12 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📖</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Guide coming soon</div>
            <div style={{ fontSize: 13 }}>Instructions will appear here once added from the admin panel.</div>
          </div>
        )}
        {sections.map((s, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: s.image ? "1fr 1fr" : "1fr", gap: 48, marginBottom: 72, alignItems: "start" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--ink)", border: "1px solid var(--gold-b)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontFamily: "Playfair Display, serif", fontSize: 14, fontWeight: 700, color: "var(--gold)" }}>{String(i+1).padStart(2,"0")}</span>
                </div>
                <div style={{ fontFamily: "Playfair Display, serif", fontSize: 22, fontWeight: 700, color: "var(--ink)" }}>{s.title}</div>
              </div>
              <p style={{ fontSize: 15, color: "var(--muted)", lineHeight: 1.8, marginBottom: 16, whiteSpace: "pre-line" }}>{s.description}</p>
              {s.tip && (
                <div style={{ background: "var(--gold-p)", border: "1px solid var(--gold-b)", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "var(--ink)", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ color: "var(--gold)", fontWeight: 700, flexShrink: 0 }}>💡 Tip:</span>
                  <span>{s.tip}</span>
                </div>
              )}
            </div>
            {s.image && (
              <div style={{ borderRadius: 14, overflow: "hidden", border: "1px solid var(--border)", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
                <img src={s.image} alt={s.title} style={{ width: "100%", display: "block" }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [activePage, setActivePage] = useState("landing");
  const [lastPage, setLastPage] = useState("landing");
  const [legalPage, setLegalPage] = useState(null);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [cms, setCms] = useState({});
  const [cmsLoaded, setCmsLoaded] = useState(false);

  // Demo form state
  const [formData, setFormData] = useState({ business_name: "", owner_name: "", email: "", phone: "", industry: "", workers: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Custom build form state
  const [cbForm, setCbForm] = useState({ business_name: "", email: "", description: "" });
  const [cbSubmitted, setCbSubmitted] = useState(false);
  const [cbSubmitting, setCbSubmitting] = useState(false);

  // Load all CMS content on mount
  useEffect(() => {
    const sections = ["hero","pricing","features","platforms","integrations","templates","faq","blog","cta","prefooter","guide","about"];
    Promise.all(sections.map(s => fetchContent(s).then(d => [s, d]))).then(results => {
      const data = {};
      results.forEach(([s, d]) => { if (d) data[s] = d; });
      setCms(data);
      setCmsLoaded(true);
    });
  }, []);

  const changePage = (page) => { setActivePage(page); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const openComingSoon = () => setShowComingSoon(true);
  const openCustom = () => { setShowComingSoon(false); setShowCustom(true); };

  const handleDemoSubmit = async () => {
    if (!formData.business_name || !formData.email || !formData.phone) return;
    setSubmitting(true);
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/fj_demo_requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, Prefer: "return=minimal" },
        body: JSON.stringify({ business_name: formData.business_name, owner_name: formData.owner_name, email: formData.email, phone: formData.phone, industry: formData.industry, workers: formData.workers, status: "Pending", submitted_date: new Date().toISOString().split("T")[0], created_at: new Date().toISOString() }),
      });
    } catch (e) { console.error(e); }
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/send-enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ type: "demo", ...formData }),
      });
    } catch {}
    setSubmitting(false);
    setSubmitted(true);
  };

  const handleCustomSubmit = async () => {
    setCbSubmitting(true);
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/fj_demo_requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, Prefer: "return=minimal" },
        body: JSON.stringify({ business_name: cbForm.business_name, owner_name: "", email: cbForm.email, phone: "", industry: "Custom Build", workers: cbForm.description, status: "Pending", submitted_date: new Date().toISOString().split("T")[0], created_at: new Date().toISOString() }),
      });
    } catch {}
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/send-enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ type: "custom_build", ...cbForm }),
      });
    } catch {}
    setCbSubmitting(false);
    setCbSubmitted(true);
  };

  const closeLegal = () => { setLegalPage(null); setActivePage(lastPage); window.scrollTo({ top: 0 }); };
  if (legalPage === "terms") return <><style>{globalCss}</style><TermsPage onBack={closeLegal} /></>;
  if (legalPage === "privacy") return <><style>{globalCss}</style><PrivacyPage onBack={closeLegal} /></>;
  if (legalPage === "cancellation") return <><style>{globalCss}</style><CancellationPage onBack={closeLegal} /></>;

  return (
    <>
      <style>{globalCss}</style>
      <Nav activePage={activePage} setActivePage={changePage} onGetStarted={openComingSoon} />
      <div style={{ paddingTop: 64 }}>
        {activePage === "landing"      && <LandingPage onGetStarted={openComingSoon} onRequestDemo={openCustom} cms={cms} />}
        {activePage === "about"        && <AboutPage onGetStarted={openComingSoon} cms={cms.about} />}
        {activePage === "features"     && <FeaturesPage onGetStarted={openComingSoon} cms={cms.features} />}
        {activePage === "platforms"    && <PlatformsPage onGetStarted={openComingSoon} cms={cms.platforms} />}
        {activePage === "integrations" && <IntegrationsPage cms={cms.integrations} />}
        {activePage === "templates"    && <TemplatesPage onGetStarted={openComingSoon} cms={cms.templates} />}
        {activePage === "faq"          && <FAQPage cms={cms.faq} />}
        {activePage === "blog"         && <BlogPage cms={cms.blog} />}
        {activePage === "guide"        && <GuidePage cms={cms.guide} />}
      </div>
      <PreFooter
        setActivePage={changePage}
        setLegalPage={(p) => { setLastPage(activePage); setLegalPage(p); }}
        onGetStarted={openComingSoon}
        content={cms.prefooter}
      />
      <Footer />

      {showComingSoon && <ComingSoonModal onClose={() => setShowComingSoon(false)} onRequestDemo={() => { setShowComingSoon(false); setShowDemo(true); }} />}
      {showDemo && <DemoModal onClose={() => { setShowDemo(false); setSubmitted(false); setFormData({ business_name: "", owner_name: "", email: "", phone: "", industry: "", workers: "" }); }} formData={formData} setFormData={setFormData} submitted={submitted} submitting={submitting} onSubmit={handleDemoSubmit} />}
      {showCustom && <CustomModal onClose={() => { setShowCustom(false); setCbSubmitted(false); setCbForm({ business_name: "", email: "", description: "" }); }} cbForm={cbForm} setCbForm={setCbForm} cbSubmitted={cbSubmitted} cbSubmitting={cbSubmitting} onSubmit={handleCustomSubmit} />}
    </>
  );
}
